/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports -- jsx required at runtime for @jsxRuntime classic
import { css, jsx } from '@emotion/react';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { Command, RenderOptionsPropsT } from '@atlaskit/editor-common/types';
import type { FloatingToolbarDropdown } from '@atlaskit/editor-common/types';
import ButtonItem from '@atlaskit/menu/button-item';
import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Popup } from '@atlaskit/popup/popup';
import { token } from '@atlaskit/tokens';

import { SUB_TYPE_ICON_MAP } from '../remix-overlay/constants';
import type { KnownSubType } from '../remix-overlay/types';

const MENU_ITEM_HEIGHT = 32;
const HEADING_HEIGHT = 32;
const MENU_WIDTH = 175;

// ── Preview panel styles ────────────────────────────────────────────────────

const PREVIEW_WIDTH = 144;
const PREVIEW_HEIGHT = 104;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- internal only
const previewContentStyles = css({
	width: `${PREVIEW_WIDTH}px`,
	height: `${PREVIEW_HEIGHT}px`,
	boxSizing: 'border-box',
	padding: token('space.150'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'none',
});

export type RemixTypeSwitcherOnSelect = (params: { subtype: string }) => void;

/** A subtype option to render in the dropdown. */
export interface RemixTypeSwitcherSubType {
	/** Unique subtype identifier (e.g. 'mindmap', 'pie-chart'). */
	value: string;
	/** The emitType override for this subtype, if different from the parent type. */
	emitType?: string;
	/** The display label message descriptor. */
	labelMessage: MessageDescriptor;
}

export interface RemixTypeSwitcherDropdownOptions {
	/** The current remix node's dynamicUiType (e.g. 'diagram', 'chart'). */
	dynamicUiType: string;
	/** The current remix node's dynamicUiSubtype (e.g. 'mindmap', 'pie-chart'). */
	dynamicUiSubtype?: string;
	/** Display label for the current subtype (used as the dropdown button text). */
	currentLabel: string;
	/** Optional display label message descriptor for the current subtype (used as the dropdown button text). */
	currentLabelMessage?: MessageDescriptor;
	/** The group label message descriptor (displayed as the dropdown heading). */
	groupLabelMessage: MessageDescriptor;
	/** The sibling subtypes to show in the dropdown. */
	siblingSubTypes: RemixTypeSwitcherSubType[];
	/** Callback fired when the user selects a different subtype. */
	onSelect: RemixTypeSwitcherOnSelect;
	/** The intl shape for message formatting. */
	intl: IntlShape;
	/** Whether the dropdown should be disabled (e.g. during remixing). */
	disabled?: boolean;
}

// ── Inner render component (needs hooks for hover state) ────────────────────

interface TypeSwitcherMenuProps {
	siblingSubTypes: RemixTypeSwitcherSubType[];
	dynamicUiSubtype?: string;
	groupLabelMessage: MessageDescriptor;
	onSelect: RemixTypeSwitcherOnSelect;
	intl: IntlShape;
	hide: () => void;
}

function TypeSwitcherMenu({
	siblingSubTypes,
	dynamicUiSubtype,
	groupLabelMessage,
	onSelect,
	intl,
	hide,
}: TypeSwitcherMenuProps) {
	const [hoveredSubType, setHoveredSubType] = useState<string | null>(null);

	const handleMouseEnter = useCallback((value: string) => {
		setHoveredSubType(value);
	}, []);

	const handleMouseLeave = useCallback(() => {
		setHoveredSubType(null);
	}, []);

	return (
		<div data-testid="remix-type-switcher-menu-wrapper">
			<MenuGroup>
				<Section>
					<HeadingItem testId="remix-type-switcher-group-header">
						{intl.formatMessage(groupLabelMessage)}
					</HeadingItem>
					{siblingSubTypes.map((subType: RemixTypeSwitcherSubType) => {
						const isSelected = subType.value === dynamicUiSubtype;
						const isHovered = hoveredSubType === subType.value;
						const IconComponent =
							subType.value in SUB_TYPE_ICON_MAP
								? SUB_TYPE_ICON_MAP[subType.value as KnownSubType]
								: null;
						const showPreview = isHovered && IconComponent !== null;

						const button = (
							<div
								onMouseEnter={() => handleMouseEnter(subType.value)}
								onMouseLeave={handleMouseLeave}
								onFocus={() => handleMouseEnter(subType.value)}
								onBlur={handleMouseLeave}
							>
								<ButtonItem
									testId={`remix-type-switcher-item-${subType.value}`}
									isSelected={isSelected}
									onClick={() => {
										if (isSelected) {
											hide();
											return;
										}
										onSelect({ subtype: subType.value });
										hide();
									}}
								>
									{intl.formatMessage(subType.labelMessage)}
								</ButtonItem>
							</div>
						);

						return showPreview ? (
							<Popup
								key={subType.value}
								isOpen
								placement="right"
								autoFocus={false}
								shouldDisableFocusLock
								trigger={(triggerProps) => {
									const { 'aria-expanded': _ariaExpanded, ...restTriggerProps } = triggerProps;
									return (
										<div
											{...restTriggerProps}
											onMouseEnter={() => handleMouseEnter(subType.value)}
											onMouseLeave={handleMouseLeave}
											onFocus={() => handleMouseEnter(subType.value)}
											onBlur={handleMouseLeave}
										>
											<ButtonItem
												testId={`remix-type-switcher-item-${subType.value}`}
												isSelected={isSelected}
												onClick={() => {
													if (isSelected) {
														hide();
														return;
													}
													onSelect({
														subtype: subType.value,
													});
													hide();
												}}
											>
												{intl.formatMessage(subType.labelMessage)}
											</ButtonItem>
										</div>
									);
								}}
								content={() => (
									<div css={previewContentStyles} data-testid="remix-type-switcher-preview">
										<IconComponent alt={intl.formatMessage(subType.labelMessage)} />
									</div>
								)}
							/>
						) : (
							<React.Fragment key={subType.value}>{button}</React.Fragment>
						);
					})}
				</Section>
			</MenuGroup>
		</div>
	);
}

/**
 * Builds a `FloatingToolbarDropdown<Command>` item for switching the remix
 * node's subtype from the floating selection toolbar.
 *
 * Uses a custom render function for the dropdown options, rendering via
 * ADS `HeadingItem` (for the group header) and `ButtonItem` (for subtypes)
 * directly. This gives us full control over layout — no icon slots, no
 * leading gap, proper non-interactive bold heading.
 *
 * The button label shows the current subtype (e.g. "Mindmap"), and the
 * dropdown menu groups sibling subtypes under the parent category heading
 * (e.g. "Diagrams").
 *
 * When hovering over a subtype item, a preview panel appears to the right
 * showing the subtype's icon — the same icons used in the remix overlay.
 *
 * Returns `null` when the remix type has no switchable subtypes (e.g. custom,
 * slides, whiteboard, database).
 */
export function buildRemixTypeSwitcherDropdown({
	dynamicUiType,
	dynamicUiSubtype,
	currentLabel,
	currentLabelMessage,
	groupLabelMessage,
	siblingSubTypes,
	onSelect,
	intl,
	disabled = false,
}: RemixTypeSwitcherDropdownOptions): FloatingToolbarDropdown<Command> | null {
	if (siblingSubTypes.length === 0) {
		return null;
	}

	const height = HEADING_HEIGHT + siblingSubTypes.length * MENU_ITEM_HEIGHT;
	return {
		id: 'editor.malleable-ui.remix-type-switcher',
		type: 'dropdown',
		title: currentLabelMessage ? intl.formatMessage(currentLabelMessage) : currentLabel,
		options: {
			width: MENU_WIDTH,
			height,
			render: ({ hide }: RenderOptionsPropsT<Command>) => (
				<TypeSwitcherMenu
					siblingSubTypes={siblingSubTypes}
					dynamicUiSubtype={dynamicUiSubtype}
					groupLabelMessage={groupLabelMessage}
					onSelect={onSelect}
					intl={intl}
					hide={hide}
				/>
			),
		},
		showSelected: false,
		disabled,
		testId: 'editor-floating-toolbar__remix-type-switcher',
	};
}
